import { useCallback } from 'react';
import {
	type AssigneeFilterOption,
	BinaryStatusCategory,
	type ProjectFilterOption,
	useFilterContext,
} from '../filter-context';
import { type BinaryStatusCategoryFilterOption } from '../filter-context/filter-context';
import { ISSUE_NAVIGATOR_URL } from '../utils';

const buildJQLAdvancedIssueUrl = (jql: string) => `/issues/?jql=${jql}`;

const buildJiraSearchUrl = (query: string) =>
	`/jira/search?searchString=${encodeURIComponent(query)}`;

const buildSmartQueryAdvancedSearchUrl = (query: string) =>
	`/secure/QuickSearch.jspa?searchString=${encodeURIComponent(query)}`;

const buildJqlQuery = (
	query: string,
	projectIds: string[],
	accountIds: string[],
	binaryStatusCategories: string[],
) => {
	const projectJQL =
		projectIds.length > 0 ? ` AND project IN (${encodeURIComponent(projectIds.join(','))})` : '';
	const assigneeJQL =
		accountIds.length > 0 ? ` AND assignee IN (${encodeURIComponent(accountIds.join(','))})` : '';

	const binaryStatusCategoryValues = binaryStatusCategories
		.map((id) => {
			switch (id) {
				case BinaryStatusCategory.DONE.id:
					return BinaryStatusCategory.DONE.jqlValue;
				case BinaryStatusCategory.OPEN.id:
					return BinaryStatusCategory.OPEN.jqlValue;
				default:
					return '';
			}
		})
		.join(', ');

	const binaryStatusCategoryJQL =
		binaryStatusCategories.length === 1
			? ` AND statusCategory IN (${encodeURIComponent(binaryStatusCategoryValues)})`
			: '';

	return `text ~ "${encodeURIComponent(
		query,
	)}"${projectJQL}${assigneeJQL}${binaryStatusCategoryJQL}`;
};

const buildJiraAdvancedSearchPath = (
	query: string,
	availableProjectFilters: ProjectFilterOption[],
	availableAssigneeFilters: AssigneeFilterOption[],
	availableBinaryStatusCategories: BinaryStatusCategoryFilterOption[],
) => {
	const trimmedQuery = query.trim();
	if (!trimmedQuery) {
		// If query is empty, we utilise a spa transition with ISSUE_NAVIGATOR_URL
		return query === '' ? ISSUE_NAVIGATOR_URL : buildSmartQueryAdvancedSearchUrl(query);
	}

	const projectIds = availableProjectFilters.filter((f) => f.isChecked).map((f) => f.id);
	const accountIds = availableAssigneeFilters.filter((f) => f.isChecked).map((f) => f.id);
	const binaryStatusCategories = availableBinaryStatusCategories
		.filter((f) => f.isChecked)
		.map((f) => f.id);

	/*
    If no filters are specified, we route the user to the "smart" query url (/secure/QuickSearch.jspa)
    as we want to retain the smart querying functions it has (e.g. routing you to an issue directly if you enter an issue key)
    */
	if (projectIds.length === 0 && accountIds.length === 0 && binaryStatusCategories.length === 0) {
		return buildJiraSearchUrl(trimmedQuery);
	}

	return buildJQLAdvancedIssueUrl(
		buildJqlQuery(trimmedQuery, projectIds, accountIds, binaryStatusCategories),
	);
};

export const buildJiraAdvancedSearchUrl = (
	query: string,
	availableProjectFilters: ProjectFilterOption[],
	availableAssigneeFilters: AssigneeFilterOption[],
	availableBinaryStatusCategories: BinaryStatusCategoryFilterOption[],
	siteUrl?: string,
) => {
	return `${siteUrl ?? ''}${buildJiraAdvancedSearchPath(
		query,
		availableProjectFilters,
		availableAssigneeFilters,
		availableBinaryStatusCategories,
	)}`;
};

export const useFiltersAdvancedSearchUrlFactory = () => {
	const {
		projectFilters: { availableFilters: availableProjectFilters },
		assigneeFilters: { availableFilters: availableAssigneeFilters },
		binaryStatusCategoryFilters: { availableFilters: availableBinaryStatusCategoryFilters },
	} = useFilterContext();

	return useCallback(
		(query: string, siteUrl?: string) => {
			return buildJiraAdvancedSearchUrl(
				query,
				availableProjectFilters,
				availableAssigneeFilters,
				availableBinaryStatusCategoryFilters,
				siteUrl,
			);
		},
		[availableProjectFilters, availableAssigneeFilters, availableBinaryStatusCategoryFilters],
	);
};

export const useQueryOnlyAdvancedSearchUrlFactory = () => {
	return useCallback(
		(query: string, siteUrl?: string) => buildJiraAdvancedSearchUrl(query, [], [], [], siteUrl),
		[],
	);
};
